import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import Eyebrow from '../components/ui/Eyebrow';
import HeroBackground from '../components/ui/HeroBackground';
import ManagerAvatar from '../components/ui/ManagerAvatar';
import { managerSlug } from '../utils/slug';
import { formatScore, loadSeasonVault } from '../utils/seasonVault';
import { Reveal } from '../motion';

export default function Archive() {
  const params = useParams();
  const filterYear = parseInt(params.year, 10) || null;
  const [vault, setVault] = useState([]);

  useEffect(() => {
    loadSeasonVault()
      .then(setVault)
      .catch(() => setVault([]));
  }, []);

  const visible = filterYear
    ? vault.filter((season) => season.year === filterYear)
    : vault;

  const latest = vault[0];

  return (
    <>
      <HeroBackground variant="gold" backdrop="studio" className="px-6 md:px-14 py-14">
        <Eyebrow>ARCHIVE</Eyebrow>
        <h1 className="font-display font-black uppercase tracking-normal mt-3.5 leading-[0.88]" style={{ fontSize: 'clamp(48px, 8vw, 104px)' }}>
          Season vault.
          <br />
          <span className="text-accent-gold">Every year has receipts.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base md:text-lg text-ink-secondary leading-relaxed italic">
          Championships, regular-season flexes, title-game margins, highest scores, and the closest
          weekly sweat from each completed season.
        </p>
        {latest && (
          <div className="mt-7 flex flex-wrap gap-8 border-t border-border pt-5 text-[10px] tracking-[0.2em] text-ink-tertiary">
            <span><strong className="text-accent-gold">{vault.length}</strong> SEASONS FILED</span>
            <span><strong className="text-accent-gold">{latest.champion}</strong> DEFENDING CHAMP</span>
            <span><strong className="text-accent-gold">{latest.regularSeason?.manager}</strong> LATEST REGULAR-SEASON LEADER</span>
          </div>
        )}
      </HeroBackground>

      <section className="px-6 md:px-14 py-6 border-b border-border bg-bg-deep">
        <div className="flex flex-wrap gap-2">
          <Link
            to="/archive"
            className={`text-[10px] font-extrabold tracking-[0.2em] px-3.5 py-2 border ${
              !filterYear
                ? 'bg-accent-gold text-bg-base border-accent-gold'
                : 'border-border text-ink-tertiary hover:border-accent-gold hover:text-accent-gold'
            }`}
          >
            ALL
          </Link>
          {vault.map((season) => (
            <Link
              key={season.year}
              to={`/archive/${season.year}`}
              className={`text-[10px] font-extrabold tracking-[0.2em] px-3.5 py-2 border ${
                season.year === filterYear
                  ? 'bg-accent-gold text-bg-base border-accent-gold'
                  : 'border-border text-ink-tertiary hover:border-accent-gold hover:text-accent-gold'
              }`}
            >
              {season.year}
            </Link>
          ))}
        </div>
      </section>

      <section className="px-6 md:px-14 py-14">
        <Reveal className="mb-9">
          <Eyebrow color="gold">SEASON VAULT</Eyebrow>
          <h2 className="mt-3 font-display text-display-xs md:text-display-sm font-black tracking-normal leading-none">
            {filterYear ? `${filterYear} on record.` : 'The completed seasons, one file at a time.'}
          </h2>
        </Reveal>

        {visible.length === 0 ? (
          <div className="bg-bg-surface p-6 border-l-[3px] border-accent-red text-sm text-ink-secondary">
            No archive record found for {filterYear || 'this filter'}.
          </div>
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            {visible.map((season, index) => (
              <Reveal key={season.year} delay={80 + (index % 2) * 60}>
                <SeasonVaultCard season={season} />
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function SeasonVaultCard({ season }) {
  return (
    <article className="bg-bg-surface border-t-[3px] border-accent-gold">
      <div className="p-6 md:p-7 grid grid-cols-1 sm:grid-cols-[96px_minmax(0,1fr)] gap-5">
        <Link to={`/managers/${managerSlug(season.champion)}`} className="justify-self-start">
          <ManagerAvatar name={season.champion} size="lg" framed />
        </Link>

        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-2">
            <div className="text-[10px] text-accent-gold tracking-[0.3em] font-extrabold">
              SEASON · {season.year}
            </div>
            <div className="text-[10px] text-ink-tertiary tracking-[0.2em]">
              {season.teams} TEAMS
            </div>
          </div>
          <Link
            to={`/managers/${managerSlug(season.champion)}`}
            className="text-3xl md:text-4xl font-black tracking-normal leading-none hover:text-accent-gold transition-colors break-words"
          >
            {season.champion?.toUpperCase()}.
          </Link>
          <div className="mt-2 text-sm text-ink-secondary italic leading-relaxed">
            {season.championTeam} won the playoff title
            {season.championRecord ? ` with a ${season.championRecord} season label` : ''}.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 border-t border-border">
        <ArchiveStat
          label="TITLE GAME"
          title={season.titleGame
            ? `${season.titleGame.winner} over ${season.titleGame.runnerUp}`
            : 'Title game unavailable'}
          body={season.titleGame
            ? `${formatScore(season.titleGame.winnerScore)} to ${formatScore(season.titleGame.runnerUpScore)} · ${formatScore(season.titleGame.margin)}-point margin.`
            : 'The playoff file does not include a title-game row for this season.'}
        />
        <ArchiveStat
          label="REGULAR SEASON"
          title={season.regularSeason
            ? `${season.regularSeason.manager} led the table`
            : 'Regular-season leader unavailable'}
          body={season.regularSeason
            ? `${season.regularSeason.wins}-${season.regularSeason.losses}, ${formatScore(season.regularSeason.pointsFor)} points for.`
            : 'The standings file does not include this season.'}
        />
        <ArchiveStat
          label="HIGHEST SCORE"
          title={season.highestScore
            ? `${season.highestScore.manager} hit ${formatScore(season.highestScore.score)}`
            : 'Highest score unavailable'}
          body={season.highestScore
            ? `Week ${season.highestScore.week}, playing as ${season.highestScore.team}.`
            : 'The weekly matchup file does not include this season.'}
        />
        <ArchiveStat
          label="CLOSEST GAME"
          title={season.closestGame
            ? `${season.closestGame.winner} by ${formatScore(season.closestGame.margin)}`
            : 'Closest game unavailable'}
          body={season.closestGame
            ? `Week ${season.closestGame.week}: ${formatScore(season.closestGame.winnerScore)} to ${formatScore(season.closestGame.loserScore)} over ${season.closestGame.loser}.`
            : 'The weekly matchup file does not include this season.'}
        />
      </div>
    </article>
  );
}

function ArchiveStat({ label, title, body }) {
  return (
    <div className="p-5 border-b md:border-r border-border even:md:border-r-0 last:border-b-0">
      <div className="text-[9px] text-accent-gold tracking-[0.25em] font-extrabold mb-2">
        {label}
      </div>
      <div className="text-lg font-black leading-tight tracking-normal">{title}</div>
      <p className="mt-2 text-[12px] text-ink-tertiary leading-relaxed">{body}</p>
    </div>
  );
}
